import { useEffect, useState } from "react";
import { api, API_BASE, formatApiError, getToken, loadStoredToken } from "@/lib/api";
import { useAuth, roleLabel } from "@/lib/auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { QrCode, Download, RefreshCw, Clock, LogIn, LogOut, Users as UsersIcon, MapPin, Printer } from "lucide-react";

function formatDuration(start, end) {
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  const mins = Math.round((e - s) / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function QrImage({ src, alt, size = 180 }) {
  // Fetch with auth header -> blob -> object URL
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true;
    let objUrl = null;
    api.get(src, { responseType: "blob" }).then((res) => {
      if (!active) return;
      objUrl = URL.createObjectURL(res.data);
      setUrl(objUrl);
    });
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [src]);
  if (!url) {
    return <div className="animate-pulse bg-muted" style={{ width: size, height: size }} />;
  }
  return <img src={url} width={size} height={size} alt={alt} />;
}

function QrCardDialog({ open, onOpenChange, person, type = "employee" }) {
  if (!person) return null;
  const endpoint = type === "employee"
    ? `/employees/${person.id}/qr`
    : `/locations/${person.id}/qr`;

  const downloadPng = async () => {
    try {
      const res = await api.get(endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type === "employee" ? "staff" : "location"}-qr-${(person.name || "qr").replace(/\s+/g, "_")}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("QR downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const printCard = async () => {
    try {
      const res = await api.get(endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const w = window.open("", "_blank", "width=400,height=600");
      if (!w) return toast.error("Popup blocked");
      const sub = type === "employee" ? (person.position || roleLabel(person.role)) : (person.area_type || "Area");
      const org = type === "employee" ? "Sparkle Staff Credential" : "Sparkle Location Tag";
      w.document.write(`
        <html><head><title>Sparkle QR</title>
        <style>
          body{font-family:system-ui,sans-serif;display:flex;align-items:center;justify-content:center;min-height:100vh;margin:0;background:#f5f6f8}
          .card{background:white;border:1px solid #e5e7eb;border-radius:20px;padding:28px;width:320px;text-align:center;box-shadow:0 10px 30px rgba(15,27,61,.08)}
          .brand{font-size:11px;letter-spacing:2px;color:#10b981;font-weight:700;text-transform:uppercase;margin-bottom:4px}
          h1{font-size:20px;margin:0;color:#0f1b3d}
          .sub{color:#6b7280;font-size:13px;margin-top:4px}
          .qr{margin:20px auto;background:white;padding:12px;border:1px solid #e5e7eb;border-radius:12px;display:inline-block}
          .foot{font-size:11px;color:#9ca3af;margin-top:12px;letter-spacing:1px;text-transform:uppercase}
        </style></head>
        <body>
          <div class="card">
            <div class="brand">${org}</div>
            <h1>${person.name}</h1>
            <div class="sub">${sub}</div>
            <div class="qr"><img src="${url}" width="240" height="240" /></div>
            <div class="foot">Scan to ${type === "employee" ? "identify staff" : "clock in / out"}</div>
          </div>
          <script>window.onload=()=>setTimeout(()=>window.print(),300)</script>
        </body></html>
      `);
      w.document.close();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{type === "employee" ? "Staff" : "Location"} QR card</DialogTitle>
          <DialogDescription>
            {type === "employee"
              ? "Printable staff credential. Downloads as a PNG or opens a print layout."
              : "Printable location tag for staff to scan when entering or leaving this area."}
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-2xl border border-border p-5 text-center bg-gradient-to-b from-white to-muted/40">
          <div className="text-[0.65rem] tracking-widest uppercase text-primary font-bold mb-1">
            {type === "employee" ? "Sparkle Staff" : "Sparkle Location"}
          </div>
          <div className="font-display font-bold text-lg">{person.name}</div>
          <div className="text-xs text-muted-foreground mb-3">
            {type === "employee" ? (person.position || roleLabel(person.role)) : (person.area_type || "Area")}
          </div>
          <div className="inline-block p-3 bg-white rounded-xl border border-border">
            <QrImage src={endpoint} alt="QR Code" size={220} />
          </div>
          <div className="text-[0.65rem] tracking-widest uppercase text-muted-foreground mt-3">
            Scan to {type === "employee" ? "identify staff" : "clock in / out"}
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={downloadPng} className="flex-1 rounded-xl" data-testid="qr-download">
            <Download className="w-4 h-4 mr-1" />Download PNG
          </Button>
          <Button onClick={printCard} variant="outline" className="flex-1 rounded-xl" data-testid="qr-print">
            <Printer className="w-4 h-4 mr-1" />Print
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StaffDirectory() {
  const qc = useQueryClient();
  const { data: employees } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => (await api.get("/employees")).data,
  });
  const { data: openShifts } = useQuery({
    queryKey: ["open-shifts"],
    queryFn: async () => (await api.get("/shifts", { params: { open_only: true } })).data,
    refetchInterval: 15000,
  });
  const openByUser = Object.fromEntries((openShifts || []).map((s) => [s.user_id, s]));
  const [qrPerson, setQrPerson] = useState(null);

  const regenerate = async (u) => {
    if (!window.confirm(`Regenerate ${u.name}'s QR? The old card will stop working.`)) return;
    try {
      await api.post(`/employees/${u.id}/qr/regenerate`);
      toast.success("New QR generated");
      qc.invalidateQueries({ queryKey: ["employees"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
      {employees?.filter((u) => u.role !== "platform_admin").map((u) => {
        const shift = openByUser[u.id];
        return (
          <div key={u.id} className="border border-border bg-card rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all" data-testid={`staff-${u.id}`}>
            <div className="flex items-start gap-4">
              <Avatar className="w-14 h-14 border border-border">
                <AvatarImage src={u.photo_url} />
                <AvatarFallback>{u.name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-semibold text-sm truncate">{u.name}</div>
                  {shift && (
                    <Badge className="bg-primary/15 text-primary border-0 text-[0.6rem]">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary mr-1 animate-pulse" />
                      On shift
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{u.position || roleLabel(u.role)}</div>
                {shift ? (
                  <div className="mt-3 p-2.5 rounded-xl bg-primary/5 border border-primary/20 text-xs">
                    <div className="flex items-center gap-1.5 text-primary font-medium">
                      <MapPin className="w-3 h-3" /> {shift.area_name || "Unknown"}
                    </div>
                    <div className="text-muted-foreground mt-0.5 flex items-center gap-1.5">
                      <Clock className="w-3 h-3" /> {formatDuration(shift.clock_in_at)}
                      <span className="text-[0.65rem] opacity-70">· since {new Date(shift.clock_in_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-muted-foreground">Not currently on shift</div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
              <Button size="sm" variant="outline" className="rounded-xl flex-1" onClick={() => setQrPerson(u)} data-testid={`qr-${u.id}`}>
                <QrCode className="w-3.5 h-3.5 mr-1" />QR Card
              </Button>
              <Button size="sm" variant="ghost" onClick={() => regenerate(u)} data-testid={`regen-${u.id}`}>
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        );
      })}
      <QrCardDialog open={!!qrPerson} onOpenChange={(o) => !o && setQrPerson(null)} person={qrPerson} type="employee" />
    </div>
  );
}

function AttendanceLog() {
  const { data: shifts } = useQuery({
    queryKey: ["shifts"],
    queryFn: async () => (await api.get("/shifts")).data,
    refetchInterval: 20000,
  });
  const closeShift = async (id) => {
    try {
      await api.post(`/shifts/${id}/close`);
      toast.success("Shift closed");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="border border-border bg-card rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b border-border">
            <tr>
              {["Staff", "Area", "Clock in", "Clock out", "Duration", ""].map((h) => (
                <th key={h} className="text-left px-4 py-3 label-caps text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(shifts || []).map((s) => (
              <tr key={s.id} className="border-b border-border last:border-0 hover:bg-accent/20">
                <td className="px-4 py-3 font-medium">{s.user_name}</td>
                <td className="px-4 py-3 text-xs">
                  <div>{s.area_name}</div>
                  <div className="text-muted-foreground">{s.facility_name}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{new Date(s.clock_in_at).toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-xs">
                  {s.clock_out_at
                    ? new Date(s.clock_out_at).toLocaleString()
                    : <Badge className="bg-primary/15 text-primary border-0">Open</Badge>}
                </td>
                <td className="px-4 py-3 text-xs font-medium">{formatDuration(s.clock_in_at, s.clock_out_at)}</td>
                <td className="px-4 py-3 text-right">
                  {!s.clock_out_at && (
                    <Button size="sm" variant="ghost" onClick={() => closeShift(s.id)}>Close</Button>
                  )}
                </td>
              </tr>
            ))}
            {(!shifts || shifts.length === 0) && (
              <tr><td colSpan="6" className="p-12 text-center text-muted-foreground">
                <Clock className="w-8 h-8 mx-auto mb-2" />
                No shift records yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LocationQrs() {
  const [qrArea, setQrArea] = useState(null);
  const { data: areas } = useQuery({
    queryKey: ["areas-all"],
    queryFn: async () => (await api.get("/facilities/areas/all")).data,
  });
  const grouped = {};
  (areas || []).forEach((a) => {
    const key = `${a.facility_name || "Facility"} · ${a.building_name || "Building"} · ${a.floor_name || "Floor"}`;
    (grouped[key] ||= []).push(a);
  });

  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([k, as]) => (
        <div key={k}>
          <div className="text-[0.7rem] tracking-widest uppercase text-muted-foreground font-semibold mb-2">{k}</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {as.map((a) => (
              <div key={a.id} className="border border-border bg-card rounded-2xl p-4 flex items-center gap-3" data-testid={`loc-${a.id}`}>
                <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{a.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{a.area_type || "Area"}</div>
                </div>
                <Button size="sm" variant="outline" className="rounded-xl" onClick={() => setQrArea(a)}>
                  <QrCode className="w-3.5 h-3.5 mr-1" />QR
                </Button>
              </div>
            ))}
          </div>
        </div>
      ))}
      {(!areas || areas.length === 0) && (
        <div className="border border-dashed border-border rounded-2xl p-12 text-center">
          <MapPin className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <div className="font-medium">No areas defined yet</div>
          <div className="text-sm text-muted-foreground mt-1">Create facilities first.</div>
        </div>
      )}
      <QrCardDialog open={!!qrArea} onOpenChange={(o) => !o && setQrArea(null)} person={qrArea} type="location" />
    </div>
  );
}

export default function StaffManagement({ embedded = false }) {
  return (
    <div className={embedded ? "" : "p-4 md:p-8 max-w-[1400px]"}>
      {!embedded && (
        <div className="mb-8">
          <div className="label-caps text-muted-foreground mb-2">Configure</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Staff Management</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Live shift tracking, QR identification cards, and location clock-in tags.
            Every completed task is stamped with the staff member's QR and current clock-in location.
          </p>
        </div>
      )}
      {embedded && (
        <p className="text-sm text-muted-foreground mb-6">
          QR identification, live shifts, and location tags for clock-in. Every completed task is stamped with the staff member's QR and current clock-in location.
        </p>
      )}

      <Tabs defaultValue="directory">
        <TabsList className="mb-6">
          <TabsTrigger value="directory" data-testid="tab-directory">
            <UsersIcon className="w-3.5 h-3.5 mr-1.5" />Staff
          </TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">
            <Clock className="w-3.5 h-3.5 mr-1.5" />Attendance
          </TabsTrigger>
          <TabsTrigger value="locations" data-testid="tab-locations">
            <MapPin className="w-3.5 h-3.5 mr-1.5" />Location QRs
          </TabsTrigger>
        </TabsList>
        <TabsContent value="directory"><StaffDirectory /></TabsContent>
        <TabsContent value="attendance"><AttendanceLog /></TabsContent>
        <TabsContent value="locations"><LocationQrs /></TabsContent>
      </Tabs>
    </div>
  );
}
